import { success } from "App/popupMessage";
import { NODE_SERVER_IGNORED_FIELDS } from "Areas/SharedFeatures/types/nodeSource";
import getAllDocuments from "Areas/Sidebar/Documents/redux/getAllDocuments";
import compact from "lodash/compact";
import difference from "lodash/difference";
import differenceBy from "lodash/differenceBy";
import filter from "lodash/filter";
import find from "lodash/find";
import isEqual from "lodash/isEqual";
import keyBy from "lodash/keyBy";
import keys from "lodash/keys";
import map from "lodash/map";
import omit from "lodash/omit";
import { buildGetDocumentTableDataScope } from "../utils/buildActionCreatorsScope";
import locale from "./actionCreators.locale";
import actionTypes from "./actionTypes";
import api from "./api";
import { getNodesSource } from "./selectors";
let abortControllers = {};
export const abortActionCreators = {
    cancelGetDocumentTableData: (documentId) => {
        const scope = buildGetDocumentTableDataScope(documentId);
        if (!abortControllers[scope])
            return;
        abortControllers[scope].abort();
        abortControllers = omit(abortControllers, scope);
    }
};
export default {
    getAvailableDocumentObjectIds: (documentId) => ({
        type: actionTypes.GET_AVAILABLE_DOCUMENT_OBJECT_IDS,
        payload: {
            promise: () => api.getAvailableDocumentObjectIds(documentId)
        },
        meta: { documentId },
        scope: `${actionTypes.GET_AVAILABLE_DOCUMENT_OBJECT_IDS}_${documentId}`
    }),
    setSelectDocumentObjectIds: (documentId, documentObjectIds) => ({
        type: actionTypes.SET_SELECTED_DOCUMENT_OBJECT_IDS,
        documentId,
        documentObjectIds
    }),
    getDocumentTableData: (documentId, documentObjectIdFilter, nodeIds, completed, saveToStore) => {
        const scope = buildGetDocumentTableDataScope(documentId);
        if (abortControllers[scope])
            abortControllers[scope].abort();
        abortControllers[scope] = new AbortController();
        return {
            type: actionTypes.GET_DOCUMENT_TABLE_DATA,
            payload: {
                promise: () => api.getDocumentTableData(documentId, documentObjectIdFilter, nodeIds, abortControllers[scope])
            },
            meta: { documentId, nodeIds, documentObjectIdFilter, saveToStore: Boolean(saveToStore) },
            scope,
            completed: (_, { payload }) => {
                abortControllers = omit(abortControllers, scope);
                completed && completed(payload.Rows, payload.Headers);
            },
            rejected: () => {
                abortControllers = omit(abortControllers, scope);
            }
        };
    },
    saveDocumentTableData: (documentId, initialNodesSource, nodesSource, completed) => (dispatch, getState) => {
        const document = find(getAllDocuments(getState()), ["Id", documentId]);
        if (!document)
            return undefined;
        return dispatch({
            type: actionTypes.SAVE_DOCUMENT_TABLE_DATA,
            payload: {
                promise: () => api.saveDocumentTableData({
                    Id: documentId,
                    DeletedNodeIds: [],
                    NodesSource: compact(map(nodesSource, item => {
                        const prev = find(initialNodesSource, ["NodeId", item.NodeId]);
                        if (!prev)
                            return item;
                        const prevOmitted = omit(prev, NODE_SERVER_IGNORED_FIELDS);
                        if (isEqual(prevOmitted, item))
                            return undefined;
                        if (isEqual(prevOmitted.AttributeValues, item.AttributeValues))
                            return {
                                ...item,
                                AttributeValues: undefined
                            };
                        const prevAttributeValues = keyBy(prevOmitted.AttributeValues, "Key");
                        const deletedAttributeValues = differenceBy(prevOmitted.AttributeValues, item.AttributeValues || [], "Key");
                        return {
                            ...item,
                            AttributeValues: [
                                ...map(deletedAttributeValues, e => ({ ...e, Value: undefined })),
                                ...filter(item.AttributeValues, e => !isEqual(e, prevAttributeValues[e.Key]))
                            ]
                        };
                    }))
                })
            },
            meta: {
                documentId,
                nodesSource
            },
            scope: `${actionTypes.SAVE_DOCUMENT_TABLE_DATA}_${documentId}`,
            completed: (disp) => {
                disp(success(locale.buildDocumentSavedSuccessfully(document.Name)));
                completed && completed();
            },
            rejectedMessage: locale.buildDocumentSavingFailed(document.Name)
        });
    },
    updateTableData: (documentId, diff) => ({
        type: actionTypes.UPDATE_TABLE_DATA,
        documentId,
        diff
    }),
    setNodeSources: (documentId, nodesSource, onlyDiffSources, updateInitialNodes) => ({
        type: actionTypes.SET_NODE_SOURCES,
        documentId,
        nodesSource,
        onlyDiffSources,
        updateInitialNodes
    }),
    clearData: (documentId) => ({
        type: actionTypes.CLEAR_DATA,
        documentId
    }),
    setDetailedPanelOpen: (detailedPanelOpen) => ({
        type: actionTypes.SET_DETAILED_PANEL_OPEN,
        detailedPanelOpen
    }),
    getNodeSources: (documentId, nodeIds, completed) => (dispatch, getState) => {
        const document = find(getAllDocuments(getState()), ["Id", documentId]);
        if (!document)
            return undefined;
        if (nodeIds.length === 0) {
            completed && completed();
            return undefined;
        }
        const notLoaded = difference(nodeIds, keys(getNodesSource(getState())));
        if (notLoaded.length === 0) {
            completed && completed();
            return undefined;
        }
        return dispatch({
            type: actionTypes.GET_NODE_SOURCES,
            payload: {
                promise: () => api.getNodeSources(documentId, notLoaded)
            },
            meta: { documentId, notLoaded },
            scope: `${actionTypes.GET_NODE_SOURCES}_${documentId}`,
            completed: () => {
                completed && completed();
            }
        });
    },
    setTableFilters: (documentId, filtersJson) => ({
        type: actionTypes.SET_TABLE_FILTERS,
        documentId,
        filtersJson
    }),
    setTableSortOptions: (documentId, dataField, sortOrder) => ({
        type: actionTypes.SET_TABLE_SORT_OPTIONS,
        documentId,
        dataField,
        sortOrder
    })
};
